import { toTitleCase } from "./titleCase";
import { fieldsForSubject } from "../config/subjectSetupFields";

const MANAGED_CONTACT_NAME = /emergency contact name/i;
const MANAGED_CONTACT_PHONE = /emergency contact phone/i;

const AGE_MATCH = /\bage\b|उम्र|आयु|years?\s*old/i;
const GENDER_MATCH = /gender|\bsex\b|लिंग/i;

export function formatDisplayValue(value, fallback = "—") {
  const text = (value ?? "").toString().trim();
  return text || fallback;
}

export function findProfileField(sections, matcher) {
  for (const section of sections || []) {
    for (const item of section.items || []) {
      if (matcher(item.key)) return item.value?.trim() || "";
    }
  }
  return "";
}

export function extractAge(sections) {
  return findProfileField(sections, (key) => AGE_MATCH.test(key));
}

export function extractGender(sections) {
  return findProfileField(sections, (key) => GENDER_MATCH.test(key));
}

/** "34" -> "34 Years"; free text such as "34 years" or "12 months" is kept as typed. */
export function formatAge(raw, t) {
  const value = (raw || "").trim();
  if (!value) return "";
  return /^\d{1,3}$/.test(value) ? `${value} ${t("public.ageYears")}` : value;
}

export function formatGender(raw, t) {
  const value = (raw || "").trim();
  if (!value) return "";
  if (/^(m|male|man|पुरुष)$/i.test(value)) return t("public.fieldGenderMale");
  if (/^(f|female|woman|महिला|स्त्री)$/i.test(value)) return t("public.fieldGenderFemale");
  return toTitleCase(value);
}

export function formatLastUpdated(date, locale = "en-IN") {
  if (!date) return null;
  try {
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(date));
  } catch {
    return null;
  }
}

export function categorizeDetailSections(detailSections = []) {
  const medical = [];
  const personal = [];
  const emergency = [];
  const other = [];

  for (const section of detailSections) {
    const cat = (section.category || "").toLowerCase();
    const name = (section.name || "").toLowerCase();
    if (cat === "emergency") {
      emergency.push(section);
    } else if (cat === "medical" || /medical|medicine|doctor|hospital|insurance|allerg/i.test(name)) {
      medical.push(section);
    } else if (
      cat === "personal" ||
      cat === "identification" ||
      /personal|identification|aadhaar|address|language|gender/i.test(name)
    ) {
      personal.push(section);
    } else {
      other.push(section);
    }
  }

  return { medical, personal, emergency, other };
}

export function getSnapshotFields({ essentials, primaryContact, medicalProfile, isPremium, sections, t }) {
  const byId = Object.fromEntries((essentials || []).map((e) => [e.id, e]));
  const na = t("public.notAvailable");

  const bloodGroup = formatDisplayValue(byId.bloodGroup?.value, na);
  const allergies =
    isPremium && medicalProfile?.allergies?.length
      ? medicalProfile.allergies.map((a) => `${a.allergen} (${a.severity})`).join(", ")
      : formatDisplayValue(byId.allergies?.value, na);
  const conditions =
    isPremium && medicalProfile?.conditions?.length
      ? medicalProfile.conditions.join(", ")
      : formatDisplayValue(byId.condition?.value, na);
  const medications =
    isPremium && medicalProfile?.medications?.length
      ? medicalProfile.medications.join(", ")
      : findFromSections(sections, (key) => /medication|medicine|दवा/i.test(key));

  return {
    bloodGroup,
    allergies,
    conditions,
    medications,
    primaryContact,
  };
}

function flattenSectionItems(sections) {
  return (sections || []).flatMap((section) =>
    (section.items || []).map((item) => ({
      ...item,
      sectionLocked: section.locked,
    }))
  );
}

function findFromSections(sections, matcher) {
  for (const item of flattenSectionItems(sections)) {
    if (matcher(item.key) && (item.value || "").trim()) {
      return item.value.trim();
    }
  }
  return "";
}

function covers(items, matcher) {
  return items.some((item) => matcher(item.key || ""));
}

/**
 * Public medical rows are the fields the owner saved, in that order, with the
 * label and value they typed. A fixed template was renaming "Medications" to
 * "Medical Conditions" and filling Doctor / Hospital with "Not Available".
 */
export function buildMedicalItems({ medicalProfile, isPremium, medicalSections, t }) {
  const items = [];

  for (const section of medicalSections || []) {
    const ordered = [...(section.items || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    for (const item of ordered) {
      const key = (item.key || "").trim();
      if (!key) continue;
      if (item.locked || section.locked) {
        items.push({ key, value: t("public.premiumFieldLocked"), locked: true });
        continue;
      }
      const value = (item.value || "").trim();
      if (!value) continue;
      items.push({ key, value });
    }
  }

  if (isPremium && medicalProfile) {
    if (!covers(items, (key) => /allerg|एलर्जी/i.test(key)) && medicalProfile.allergies?.length) {
      items.push({
        key: t("public.allergiesLabel"),
        value: medicalProfile.allergies.map((a) => `${a.allergen} (${a.severity})`).join(", "),
      });
    }
    if (
      !covers(items, (key) => /condition|स्थिति|चिकित्सा/i.test(key)) &&
      medicalProfile.conditions?.length
    ) {
      items.push({
        key: t("public.conditionsLabel"),
        value: medicalProfile.conditions.join(", "),
      });
    }
    if (
      !covers(items, (key) => /medication|medicine|दवा/i.test(key)) &&
      medicalProfile.medications?.length
    ) {
      items.push({
        key: t("public.activeMedications"),
        value: medicalProfile.medications.join(", "),
      });
    }
  }

  return items;
}

export function buildPersonalItems({ customerName, allSections, personalSections, essentials, t }) {
  const na = t("public.notAvailable");
  const byId = Object.fromEntries((essentials || []).map((e) => [e.id, e]));
  const skipKey = (key) =>
    AGE_MATCH.test(key) ||
    GENDER_MATCH.test(key) ||
    /address|(^|[\s(])पता($|[\s)])|home|घर/i.test(key) ||
    /language|भाषा/i.test(key) ||
    (/name|नाम/i.test(key) && customerName);

  const items = [
    { key: t("public.fieldName"), value: formatDisplayValue(customerName, na) },
    { key: t("public.fieldAge"), value: formatDisplayValue(formatAge(extractAge(allSections), t), na) },
    { key: t("public.fieldGender"), value: formatDisplayValue(formatGender(extractGender(allSections), t), na) },
    {
      key: t("public.fieldAddress"),
      value: formatDisplayValue(byId.address?.value, na),
    },
    {
      key: t("public.fieldLanguage"),
      value: formatDisplayValue(
        findFromSections(personalSections, (key) => /language|भाषा/i.test(key)),
        na
      ),
    },
  ];

  for (const section of personalSections) {
    for (const item of section.items || []) {
      const key = item.key || "";
      if (skipKey(key)) continue;
      items.push({
        key,
        value: formatDisplayValue(item.value, na),
        locked: item.locked || section.locked,
      });
    }
  }

  return items;
}

export function buildEmergencyDetailItems({ emergencySections = [], t }) {
  const na = t("public.notAvailable");
  const items = [];

  for (const section of emergencySections) {
    for (const item of section.items || []) {
      const key = item.key || "";
      if (!key.trim()) continue;
      const raw = (item.value || "").toString().replace(/\s/g, "").replace(/-/g, "");
      if (/^[6-9]\d{9}$/.test(raw)) continue;
      if (item.locked) {
        items.push({ key, value: t("public.premiumFieldLocked"), locked: true });
        continue;
      }
      items.push({
        key,
        value: formatDisplayValue(item.value, na),
      });
    }
  }

  return items;
}

export function isManagedSubjectType(subjectType) {
  return subjectType === "pet" || subjectType === "vehicle";
}

export function getManagedSubjectSection(sections, subjectType) {
  const category = subjectType === "pet" ? "pet" : "vehicle";
  return (sections || []).find((section) => (section.category || "").toLowerCase() === category);
}

/** Ordered public rows for pet/vehicle profiles (all fields in one list). */
export function buildManagedSubjectProfileItems(sections, subjectType) {
  const section = getManagedSubjectSection(sections, subjectType);
  if (!section) return [];
  const byKey = Object.fromEntries((section.items || []).map((item) => [item.key, item]));
  const template = fieldsForSubject(subjectType);
  const used = new Set();
  const rows = [];

  for (const field of template) {
    if (MANAGED_CONTACT_NAME.test(field.key) || MANAGED_CONTACT_PHONE.test(field.key)) continue;
    const item = byKey[field.key];
    if (!item) continue;
    used.add(field.key);
    rows.push({
      id: item.id,
      key: field.label || field.key,
      value: item.value,
      protectedPhone: item.protectedPhone,
    });
  }

  for (const item of section.items || []) {
    if (used.has(item.key)) continue;
    if (MANAGED_CONTACT_NAME.test(item.key) || MANAGED_CONTACT_PHONE.test(item.key)) continue;
    rows.push({
      id: item.id,
      key: item.key,
      value: item.value,
      protectedPhone: item.protectedPhone,
    });
  }

  return rows;
}

export function resolveManagedPrimaryContact(sections, subjectType, callingTargets = []) {
  const primary = callingTargets.find((target) => target.role === "primary");
  if (primary) {
    return { id: primary.id, name: primary.label };
  }
  const section = getManagedSubjectSection(sections, subjectType);
  if (!section) return null;
  const nameItem = (section.items || []).find((item) => MANAGED_CONTACT_NAME.test(item.key));
  const phoneItem = (section.items || []).find((item) => MANAGED_CONTACT_PHONE.test(item.key));
  if (!phoneItem) return null;
  return {
    id: phoneItem.id,
    name: nameItem?.value?.trim() || "Emergency Contact",
  };
}

export function buildOtherDetailItems({ otherSections = [], t }) {
  const na = t("public.notAvailable");
  const items = [];

  for (const section of otherSections) {
    for (const item of section.items || []) {
      const key = item.key || "";
      if (!key.trim()) continue;
      if (item.locked) {
        items.push({ key, value: t("public.premiumFieldLocked"), locked: true });
        continue;
      }
      items.push({
        key,
        value: formatDisplayValue(item.value, na),
      });
    }
  }

  return items;
}
